import React, { useState } from 'react';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  TableState,
  useReactTable,
} from '@tanstack/react-table';
import { applyFilter, HumanOperator } from '../../Filters/constants';
import { TableFilterValue } from './filterTypes';
import { Checkbox } from '@ais-ui/ui-core';

type FilterState = {
  [key: string]: { operator: HumanOperator; value: string };
};

interface Identifiable {
  id: number | string;
}

const useRowSelection = (currentPageData: any[]) => {
  const [selectedRowIds, setSelectedRowIds] = useState<
    Record<string | number, boolean>
  >({});

  const toggleRowSelected = (
    rowId: string | number,
    shouldSelectAll: boolean | null = null
  ) => {
    setSelectedRowIds((prev) => {
      const newSelected: Record<string | number, boolean> = { ...prev };
      if (shouldSelectAll !== null) {
        if (shouldSelectAll) {
          currentPageData.forEach((row) => {
            newSelected[(row as Identifiable).id] = true;
          });
        } else {
          currentPageData.forEach((row) => {
            delete newSelected[(row as Identifiable).id];
          });
        }
      } else {
        if (newSelected[rowId]) {
          delete newSelected[rowId]; // Deselect if already selected
        } else {
          newSelected[rowId] = true; // Select if not already selected
        }
      }
      return newSelected;
    });
  };

  const selectColumn = {
    id: 'select',
    accessorKey: 'id',
    header: ({ table }: { table: any }) => {
      const allPageRowsSelected = table
        .getRowModel()
        .rows.every((row: any) => selectedRowIds[row.original.id]);
      const somePageRowsSelected = table
        .getRowModel()
        .rows.some((row: any) => !!selectedRowIds[row.original.id]);

      return (
        <Checkbox
          checked={
            allPageRowsSelected
              ? true
              : somePageRowsSelected
              ? 'indeterminate'
              : false
          }
          onCheckedChange={(value) => {
            const shouldSelectAll = !!value;
            table.getRowModel().rows.forEach((row: any) => {
              toggleRowSelected(row.original.id, shouldSelectAll);
            });
          }}
          aria-label="Select all"
        />
      );
    },
    cell: ({ row }: { row: any }) => (
      <div>
        <Checkbox
          checked={!!selectedRowIds[row.original.id]}
          onCheckedChange={() => {
            toggleRowSelected(row.original.id);
          }}
          aria-label="Select row"
        />
      </div>
    ),
  };

  return { selectedRowIds, setSelectedRowIds, selectColumn };
};

export const useShipTable = (
  data: any[],
  columns: ColumnDef<any, any>[],
  isSelectableReportType: boolean
) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({});
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 50;

  const onApplyFilter = (
    column: string,
    operator: HumanOperator,
    value: string
  ) => {
    // Create a filter value as a tuple [operator, value]
    const filterValue: TableFilterValue = { operator: operator, value: value };

    // Find the column and set the filter value
    const columnInstance = table.getColumn(column);
    if (columnInstance) {
      columnInstance.setFilterValue(filterValue);
      setAppliedFilters((prev) => ({ ...prev, [column]: { operator, value } }));
    }
  };

  const onRemoveFilter = (column: string) => {
    setAppliedFilters((prev) => {
      // Check if the column exists in the current filters
      if (prev[column]) {
        // Get the column instance from the table
        const columnInstance = table.getColumn(column);
        if (columnInstance) {
          // Clear the filter value for the column
          columnInstance.setFilterValue({ operator: null, value: null });
        }

        // Create a new state without the filter for this column
        const { [column]: _, ...newState } = prev;
        return newState;
      }

      // Return the previous state if the column does not exist
      return prev;
    });
  };

  const filteredData = React.useMemo(() => {
    if (!appliedFilters || Object.keys(appliedFilters).length === 0) {
      return data;
    }
    return data.filter((row) => {
      return Object.keys(appliedFilters).every((column) => {
        const filter = appliedFilters[column];
        const rowValue = row[column];

        return applyFilter(rowValue, filter.value, filter.operator);
      });
    });
  }, [data, appliedFilters]);

  const sortedData = React.useMemo(() => {
    if (!sorting.length) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      for (const { id, desc } of sorting) {
        if (a[id] > b[id]) return desc ? -1 : 1;
        if (a[id] < b[id]) return desc ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [filteredData, sorting]);

  const currentPageData = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [page, sortedData]);

  const { selectedRowIds, setSelectedRowIds, selectColumn } =
    useRowSelection(currentPageData);

  const table = useReactTable({
    data: currentPageData,
    columns:
      isSelectableReportType && data.length > 0
        ? [selectColumn, ...columns]
        : columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: true,
    state: {
      sorting,
      filters: appliedFilters,
    } as Partial<TableState | FilterState>,
  });

  return {
    table,
    rowsPerPage,
    currentPageData,
    filteredData,
    appliedFilters,
    onApplyFilter,
    onRemoveFilter,
    page,
    setPage,
    selectedRowIds,
    setSelectedRowIds,
  };
};
